/**
 * ParlaysView Component
 * Dedicated full-width view for tracked parlays with stats dashboard,
 * animated filters, and responsive card grid.
 */

import ErrorModal from '@components/ui/ErrorModal';
import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useLiveStats } from '@hooks/useLiveStats';
import { useParlayScoring } from '@hooks/useParlayScoring';
import type { ScoringResult } from '@hooks/useStrategyStats';
import { useStrategyStats } from '@hooks/useStrategyStats';
import type { ParlayStats, TrackedParlay, TrackedParlaysFilters } from '@hooks/useTrackedParlays';
import type { DeleteResult } from '@parlay-picker/shared/hooks';
import type { League } from '@parlay-picker/shared/league';
import { leagueToLeagueKey, parlayMatchesLeague } from '@parlay-picker/shared/league';
import { AnimatePresence, motion } from 'framer-motion';
import type { ReactElement } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExpandedParlayView } from '../picks/components/ExpandedParlayView';
import { ParlayCard } from './ParlayCard';
import { ParlaysFilterBar } from './ParlaysFilterBar';
import { ParlaysStatsGrid } from './ParlaysStatsGrid';
import { type PaginationInfo, toScoringParlay } from './utils';

// =============================================================================
// Types
// =============================================================================

interface ParlaysViewProps {
  trackedParlays: TrackedParlay[];
  stats: ParlayStats;
  pagination: PaginationInfo;
  filters: TrackedParlaysFilters;
  onFiltersChange: (filters: TrackedParlaysFilters) => void;
  onRemove: (id: number) => Promise<DeleteResult>;
  onShare?: (parlay: TrackedParlay) => void;
  onLoadMore: () => void;
  loadingMore: boolean;
  loadError: string | null;
  onRefresh: () => Promise<void>;
  onSettlementComplete?: () => void;
  /** A parlay to expand and scroll to (a notification link), until onOpenParlayHandled */
  openParlayId?: number | null;
  onOpenParlayHandled?: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function ParlaysView({
  trackedParlays,
  stats,
  pagination,
  filters,
  onFiltersChange,
  onRemove,
  onShare,
  onLoadMore,
  loadingMore,
  loadError,
  onRefresh,
  onSettlementComplete,
  openParlayId = null,
  onOpenParlayHandled,
}: ParlaysViewProps): ReactElement {
  // Admin only: they gate the expanded view's score row (the lab endpoints 403 otherwise)
  const { user } = useAuth();
  const { stats: strategyStats, predictionStats } = useStrategyStats({
    enabled: user?.role === 'admin',
  });

  // Live stats subscription for real-time updates
  const {
    getPlayerLiveStats,
    getGameScores,
    getQuarterScores,
    hasLiveData,
    isGameLive,
    isGameFinal,
    canRemoveLeg,
    isPlayerDNP,
    isConnected,
    liveGamesCount,
    trackedGamesCount,
  } = useLiveStats(trackedParlays, onSettlementComplete);

  // Batch-score all tracked parlays
  const { scores: batchScores, scoreParlays: batchScoreParlays } = useParlayScoring();

  const scoringParlays = useMemo(() => trackedParlays.map(toScoringParlay), [trackedParlays]);

  useEffect(() => {
    if (scoringParlays.length > 0) {
      batchScoreParlays(scoringParlays);
    }
  }, [scoringParlays, batchScoreParlays]);

  const scoreByIndex = useMemo(() => {
    const map = new Map<number, ScoringResult>();
    trackedParlays.forEach((parlay, i) => {
      if (batchScores[i]) {
        map.set(parlay.id, batchScores[i]);
      }
    });
    return map;
  }, [trackedParlays, batchScores]);

  // Client-side filters
  const [showComparison, setShowComparison] = useState(false);
  const [leagueFilter, setLeagueFilter] = useState<League | null>(null);
  const [expandedParlayId, setExpandedParlayId] = useState<number | null>(null);

  const { showToast } = useToast();
  const handleRemove = useCallback(
    async (id: number) => {
      const result = await onRemove(id);
      if (!result.success) {
        showToast(result.error ?? 'Failed to delete parlay', 'error');
      }
    },
    [onRemove, showToast],
  );

  // Error modal state
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => {
    if (loadError) setErrorDismissed(false);
  }, [loadError]);

  const filteredParlays = useMemo(
    () =>
      leagueFilter
        ? trackedParlays.filter((parlay) =>
            parlayMatchesLeague(parlay.league, leagueToLeagueKey(leagueFilter)),
          )
        : trackedParlays,
    [trackedParlays, leagueFilter],
  );

  // A linked parlay opens like a tapped card, then its card (not the panel below it) is scrolled to
  const linkedParlayIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (openParlayId === null) return;
    if (!filteredParlays.some((parlay) => parlay.id === openParlayId)) setLeagueFilter(null);
    linkedParlayIdRef.current = openParlayId;
    setExpandedParlayId(openParlayId);
    onOpenParlayHandled?.();
  }, [openParlayId, onOpenParlayHandled, filteredParlays]);

  // Scroll expanded view into viewport when opened
  const expandedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (expandedParlayId && expandedRef.current) {
      const isLinked = linkedParlayIdRef.current === expandedParlayId;
      linkedParlayIdRef.current = null;
      requestAnimationFrame(() => {
        if (isLinked) {
          // A ref set on a motion.div after it mounts is never attached, so the card is found by id
          expandedRef.current?.parentElement
            ?.querySelector(`[data-parlay-id="${expandedParlayId}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        expandedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }, [expandedParlayId]);

  // Infinite scroll
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && pagination.hasMore && !loadingMore) {
        onLoadMore();
      }
    },
    [pagination.hasMore, loadingMore, onLoadMore],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '200px',
      threshold: 0,
    });

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div data-testid="parlays-view" className="min-h-[60vh]">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between mb-6"
      >
        <div>
          <h2 className="text-2xl font-bold text-theme-text">My Bets</h2>
          <p className="text-sm mt-0.5 text-theme-text-secondary">
            Track, analyze, and review your parlay history
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && liveGamesCount > 0 && (
            <span className="text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 bg-theme-danger/15 text-theme-danger">
              <span className="inline-block w-2 h-2 bg-theme-danger rounded-full animate-pulse" />
              {liveGamesCount} Live
            </span>
          )}
        </div>
      </motion.div>

      {/* Stats Dashboard */}
      <ParlaysStatsGrid
        stats={stats}
        trackedParlays={trackedParlays}
        liveGamesCount={liveGamesCount}
        isConnected={isConnected}
        trackedGamesCount={trackedGamesCount}
      />

      {/* Filter Bar */}
      <ParlaysFilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        stats={stats}
        trackedParlays={trackedParlays}
        showComparison={showComparison}
        onToggleComparison={() => setShowComparison((prev) => !prev)}
        leagueFilter={leagueFilter}
        onLeagueFilterChange={setLeagueFilter}
      />

      {/* Empty States */}
      {filteredParlays.length === 0 && trackedParlays.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 rounded-xl border bg-theme-surface border-theme-border"
        >
          <div className="text-4xl mb-3">📋</div>
          <p className="text-lg font-medium mb-1 text-theme-text">No tracked parlays yet</p>
          <p className="text-sm text-theme-text-secondary">
            Generate picks or build custom parlays, then track them to monitor results here.
          </p>
        </motion.div>
      )}

      {filteredParlays.length === 0 && trackedParlays.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 rounded-xl border bg-theme-surface border-theme-border"
        >
          <div className="text-3xl mb-2">🔍</div>
          <p className="text-sm font-medium text-theme-text">
            No parlays match the selected filters
          </p>
          <p className="text-xs mt-1 text-theme-text-secondary">
            Try adjusting your status or source filters
          </p>
        </motion.div>
      )}

      {/* Card Grid */}
      {filteredParlays.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 @3xl:grid-cols-2 @7xl:grid-cols-3 gap-4"
        >
          <AnimatePresence>
            {filteredParlays.map((parlay, i) => {
              const isExpanded = expandedParlayId === parlay.id;

              return (
                <Fragment key={parlay.id}>
                  <motion.div
                    data-parlay-id={parlay.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: Math.min(i * 0.03, 0.3),
                      duration: 0.25,
                    }}
                  >
                    <ParlayCard
                      parlay={parlay}
                      isConnected={isConnected}
                      liveGamesCount={liveGamesCount}
                      isExpanded={isExpanded}
                      onClick={() =>
                        setExpandedParlayId((prev) => (prev === parlay.id ? null : parlay.id))
                      }
                      onRemove={handleRemove}
                      onShare={onShare}
                      getPlayerLiveStats={getPlayerLiveStats}
                      getGameScores={getGameScores}
                      isGameLive={isGameLive}
                      isGameFinal={isGameFinal}
                      hasLiveData={hasLiveData}
                      canRemoveLeg={canRemoveLeg}
                      isPlayerDNP={isPlayerDNP}
                      showComparison={showComparison}
                      currentScores={scoreByIndex.get(parlay.id)}
                    />
                  </motion.div>
                  {isExpanded && (
                    <motion.div
                      key={`expanded-${parlay.id}`}
                      ref={expandedRef}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="col-span-full rounded-xl border overflow-hidden bg-theme-surface border-theme-border"
                    >
                      <div className="p-4">
                        <ExpandedParlayView
                          parlay={parlay}
                          onClose={() => setExpandedParlayId(null)}
                          onRemove={handleRemove}
                          strategyStats={strategyStats}
                          predictionStats={predictionStats}
                          precomputedScores={scoreByIndex.get(parlay.id) ?? null}
                          getPlayerLiveStats={getPlayerLiveStats}
                          getGameScores={getGameScores}
                          getQuarterScores={getQuarterScores}
                          isGameLive={isGameLive}
                          hasLiveData={hasLiveData}
                          canRemoveLeg={canRemoveLeg}
                          isPlayerDNP={isPlayerDNP}
                          isConnected={isConnected}
                        />
                      </div>
                    </motion.div>
                  )}
                </Fragment>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Infinite scroll trigger */}
      <div ref={loadMoreTriggerRef} className="py-4">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-4">
            <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin border-theme-primary" />
            <span className="text-sm text-theme-text-secondary">Loading more parlays...</span>
          </div>
        )}
        {!pagination.hasMore && filteredParlays.length > 0 && (
          <p className="text-center text-theme-text-tertiary text-xs py-2">
            {filteredParlays.length < trackedParlays.length
              ? `Showing ${filteredParlays.length} of ${pagination.total} parlays`
              : pagination.total > 0
                ? `Showing all ${pagination.total} parlays`
                : 'No more parlays'}
          </p>
        )}
      </div>

      {/* Error Modal */}
      {loadError && !errorDismissed && (
        <ErrorModal
          error={{
            type: 'load_error',
            message: 'Failed to load tracked parlays. Please try again.',
          }}
          onDismiss={() => setErrorDismissed(true)}
          onRetry={onRefresh}
        />
      )}
    </div>
  );
}
